import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, finalize, firstValueFrom, fromEvent, merge } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { ClickOutsideDirective } from '../../../helper/directives/click-outside.directive';
import { AppPreferencesService } from '../../../services/app-preferences';
import { Httpservice } from '../../../services/httpservice';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { ReportShellComponent } from '../../structure/report-shell/report-shell';
import { downloadReportBlob } from '../shared/report-ui.helpers';
import {
  DailyGenerationComparisonEntity,
  DailyGenerationComparisonResponse,
  DailyGenerationComparisonRow,
  DailyGenerationBsCalendarCell,
  DailyGenerationBsCalendarResponse,
  DailyGenerationDateConversionResponse,
  DailyGenerationFrequency,
  DailyGenerationOption,
  DailyGenerationPowerCell,
  DailyGenerationSource,
  DailyGenerationSourceOption,
  DailyGenerationTargetType,
} from './daily-generation-comparison.interfaces';

const DEFAULT_SOURCE_OPTIONS: DailyGenerationSourceOption[] = [
  { value: 'SCADA', label: 'Scada' },
  { value: 'ENERGY_METER', label: 'Energy Meter' },
  { value: 'GENERATOR_DECLARED', label: 'Generator Declared' },
  { value: 'OPERATION_TEAM', label: 'Operation Team' },
];
const DAILY_MAX_RANGE_DAYS = 30;
const DAILY_DEFAULT_RANGE_DAYS = 6;
const ENTITY_PAGE_SIZE = 10;
const HORIZONTAL_LOAD_THRESHOLD_PX = 80;
const FILTER_OPTION_PAGE_SIZE = 10;
type DailyGenerationDateControlName = 'date' | 'from_date' | 'to_date';

function getDateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function shiftDate(value: string, days: number): string {
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() + days);
  return getDateValue(date);
}

function formatBsDate(value: string): string {
  const parts = value.split("-");
  if (parts.length !== 3) {
    return value;
  }
  return parts[2] + "/" + parts[1] + "/" + parts[0];
}

function parseBsDate(value: string): string {
  const parts = value.split("/");
  if (parts.length !== 3) {
    return value;
  }
  return parts[2] + "-" + parts[1].padStart(2, "0") + "-" + parts[0].padStart(2, "0");
}

function getPayloadDateString(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return `${value}`;
  }
  return '';
}

@Component({
  selector: 'app-daily-generation-comparison-page',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ReportShellComponent, ClickOutsideDirective],
  templateUrl: './daily-generation-comparison.html',
  styleUrl: './daily-generation-comparison.scss',
})
export class DailyGenerationComparisonPage implements OnInit {
  @ViewChild('targetFilter') private readonly targetFilter?: ElementRef<HTMLDetailsElement>;
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filtersForm: UntypedFormGroup;
  protected readonly targetTypeOptions: Array<{ value: DailyGenerationTargetType; label: string }> = [
    { value: 'generator', label: 'Generators' },
    { value: 'substation', label: 'Substation' },
  ];
  protected readonly frequencyOptions: Array<{ value: DailyGenerationFrequency; label: string }> = [
    { value: '5_MIN', label: '5 min' },
    { value: '15_MIN', label: '15 min' },
    { value: 'HOURLY', label: 'Hourly' },
    { value: 'DAILY', label: 'Daily' },
  ];

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected errorMessage = '';
  protected generatorOptions: DailyGenerationOption[] = [];
  protected substationOptions: DailyGenerationOption[] = [];
  protected selectedTargetIds: number[] = [];
  protected targetSearchTerm = '';
  protected generatorOptionPage = 0;
  protected substationOptionPage = 0;
  protected hasMoreGeneratorOptions = true;
  protected hasMoreSubstationOptions = true;
  protected isLoadingGeneratorOptions = false;
  protected isLoadingSubstationOptions = false;
  protected sourceOptions: DailyGenerationSourceOption[] = DEFAULT_SOURCE_OPTIONS;
  protected selectedSources: DailyGenerationSource[] = DEFAULT_SOURCE_OPTIONS.map((option) => option.value);
  protected entities: DailyGenerationComparisonEntity[] = [];
  protected hasMoreEntities = false;
  protected isLoadingMoreEntities = false;
  protected rows: DailyGenerationComparisonRow[] = [];
  protected bsCalendarOpen = false;
  protected bsCalendarLoading = false;
  protected bsCalendarMonth: DailyGenerationBsCalendarResponse | null = null;
  protected activeBsDateControl: DailyGenerationDateControlName = 'date';
  protected readonly bsCalendarWeekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private reportRequestSequence = 0;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly preferencesSvc: AppPreferencesService,
  ) {
    const today = getDateValue(new Date());
    this.filtersForm = this.fb.group({
      data_frequency: ['HOURLY', Validators.required],
      date: [today, Validators.required],
      from_date: [shiftDate(today, -DAILY_DEFAULT_RANGE_DAYS), Validators.required],
      to_date: [today, Validators.required],
      target_type: ['generator', Validators.required],
    });
  }

  async ngOnInit(): Promise<void> {
    this.bindFilterListeners();
    this.listenToPreferenceChanges();
    if (this.isBsDateMode) {
      await this.convertDateControls('BS');
    }
    this.loadGeneratorOptions();
    this.loadReport();
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }
    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    const today = getDateValue(new Date());
    this.filtersForm.reset(
      {
        data_frequency: 'HOURLY',
        date: today,
        from_date: shiftDate(today, -DAILY_DEFAULT_RANGE_DAYS),
        to_date: today,
        target_type: 'generator',
      },
      { emitEvent: false },
    );
    this.selectedTargetIds = [];
    this.targetSearchTerm = '';
    this.selectedSources = DEFAULT_SOURCE_OPTIONS.map((option) => option.value);
    this.errorMessage = '';
    if (this.isBsDateMode) {
      void this.handleDateSystemChange();
      return;
    }
    this.loadReport();
  }

  protected handleExportToExcel(): void {
    if (this.isLoading || this.isExporting || !this.selectedSources.length) {
      return;
    }

    this.isExporting = true;
    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_DAILY_GENERATION_COMPARISON_REPORT, ApiMethod.POST, this.buildReportPayload(0))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (blob) => downloadReportBlob(blob, this.buildExportFileName()),
      });
  }

  protected isDailyFrequency(): boolean {
    return this.getSelectedFrequency() === 'DAILY';
  }

  protected get isBsDateMode(): boolean {
    return this.preferencesSvc.showBsDate;
  }

  protected getDateInputType(): 'date' | 'text' {
    return this.isBsDateMode ? 'text' : 'date';
  }

  protected openBsDatePicker(controlName: DailyGenerationDateControlName): void {
    if (!this.isBsDateMode) {
      return;
    }

    const wasOpen = this.bsCalendarOpen;
    const previousControl = this.activeBsDateControl;
    this.activeBsDateControl = controlName;
    if (wasOpen && previousControl === controlName) {
      this.closeBsDatePicker();
      return;
    }

    void this.loadBsCalendarForControl(controlName);
  }

  protected closeBsDatePicker(): void {
    this.bsCalendarOpen = false;
  }

  protected selectBsCalendarCell(cell: DailyGenerationBsCalendarCell): void {
    if (!cell?.bs_date) {
      return;
    }

    this.closeBsDatePicker();
    this.filtersForm.patchValue({ [this.activeBsDateControl]: formatBsDate(cell.bs_date) }, { emitEvent: true });
  }

  protected shiftBsCalendarMonth(offset: number): void {
    if (!this.bsCalendarMonth || !Number.isFinite(offset) || !offset) {
      return;
    }

    const nextMonth = this.bsCalendarMonth.bs_month + offset;
    let nextYear = this.bsCalendarMonth.bs_year;
    let normalizedMonth = nextMonth;
    if (nextMonth < 1) {
      normalizedMonth = 12;
      nextYear -= 1;
    } else if (nextMonth > 12) {
      normalizedMonth = 1;
      nextYear += 1;
    }

    void this.loadBsCalendar(`${nextYear.toString().padStart(4, '0')}-${normalizedMonth.toString().padStart(2, '0')}-01`, 'BS');
  }

  protected getSelectedTargetType(): DailyGenerationTargetType {
    return this.filtersForm.get('target_type')?.value === 'substation' ? 'substation' : 'generator';
  }

  protected getSelectedFrequency(): DailyGenerationFrequency {
    const value = this.filtersForm.get('data_frequency')?.value;
    if (value === '5_MIN' || value === '15_MIN' || value === 'DAILY') {
      return value;
    }
    return 'HOURLY';
  }

  protected getTargetFilterLabel(): string {
    return this.getSelectedTargetType() === 'substation' ? 'Select Substations' : 'Select Generators';
  }

  protected getSelectedTargetLabel(): string {
    const selectedCount = this.selectedTargetIds.length;
    if (!selectedCount) {
      return this.getSelectedTargetType() === 'substation' ? 'All Substations' : 'All Generators';
    }
    const names = this.getTargetOptions()
      .filter((option) => this.selectedTargetIds.includes(option.id))
      .map((option) => option.name);
    return names.length <= 2 ? names.join(', ') : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }

  protected getFilteredTargetOptions(): DailyGenerationOption[] {
    const query = this.targetSearchTerm.trim().toLowerCase();
    return this.getTargetOptions()
      .filter((option) => !query || option.name.toLowerCase().includes(query));
  }

  protected isTargetSelected(targetId: number): boolean {
    return this.selectedTargetIds.includes(targetId);
  }

  protected handleTargetOptionsScroll(event: Event): void {
    const menu = event.currentTarget as HTMLElement | null;
    const targetType = this.getSelectedTargetType();
    const isLoading = targetType === 'substation' ? this.isLoadingSubstationOptions : this.isLoadingGeneratorOptions;
    if (!menu || isLoading) {
      return;
    }
    const remainingScroll = menu.scrollHeight - menu.clientHeight - menu.scrollTop;
    const hasMore = targetType === 'substation' ? this.hasMoreSubstationOptions : this.hasMoreGeneratorOptions;
    if (remainingScroll <= HORIZONTAL_LOAD_THRESHOLD_PX && hasMore) {
      this.loadTargetOptionPage(targetType);
    }
  }

  protected toggleTargetSelection(targetId: number, event: Event): void {
    const checked = (event.target as HTMLInputElement | null)?.checked ?? false;
    if (checked && !this.selectedTargetIds.includes(targetId)) {
      this.selectedTargetIds = [...this.selectedTargetIds, targetId];
    } else if (!checked) {
      this.selectedTargetIds = this.selectedTargetIds.filter((id) => id !== targetId);
    }
    this.loadReport();
  }

  protected getSelectedSourceLabel(): string {
    if (this.selectedSources.length === this.sourceOptions.length) {
      return 'All Sources';
    }
    const labels = this.sourceOptions
      .filter((option) => this.selectedSources.includes(option.value))
      .map((option) => option.label);
    return labels.length <= 2 ? labels.join(', ') : `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
  }

  protected isSourceSelected(source: DailyGenerationSource): boolean {
    return this.selectedSources.includes(source);
  }

  @HostListener('document:click', ['$event'])
  protected closeTargetFilterOnOutsideClick(event: Event): void {
    const filterElement = this.targetFilter?.nativeElement;
    if (filterElement && event.target instanceof Node && !filterElement.contains(event.target)) {
      filterElement.open = false;
    }
  }

  protected toggleSourceSelection(source: DailyGenerationSource, event: Event): void {
    const checked = (event.target as HTMLInputElement | null)?.checked ?? false;
    if (checked && !this.selectedSources.includes(source)) {
      this.selectedSources = [...this.selectedSources, source];
    } else if (!checked) {
      this.selectedSources = this.selectedSources.filter((item) => item !== source);
    }
    this.loadReport();
  }

  protected getCellValue(row: DailyGenerationComparisonRow, entity: DailyGenerationComparisonEntity, source: DailyGenerationSource): string {
    const value = row.values?.[entity.key]?.[source]?.value_mwh;
    return this.formatNumber(value);
  }

  protected getPowerValue(row: DailyGenerationComparisonRow, entity: DailyGenerationComparisonEntity): string {
    return this.formatNumber(this.getPowerCell(row, entity).value);
  }

  protected getPowerCellClass(row: DailyGenerationComparisonRow, entity: DailyGenerationComparisonEntity): string {
    const editorGroup = this.getPowerCell(row, entity).editor_group;
    if (editorGroup === 'admin') {
      return 'daily-generation-table__cell--admin-edit';
    }
    if (editorGroup === 'generator') {
      return 'daily-generation-table__cell--generator-edit';
    }
    return '';
  }

  protected getValueCellClass(row: DailyGenerationComparisonRow, entity: DailyGenerationComparisonEntity, source: DailyGenerationSource): string {
    const value = row.values?.[entity.key]?.[source];
    if (value?.value_source !== "logsheet_data_details") {
      return "";
    }
    return value.editor_group === "admin"
      ? "daily-generation-table__cell--admin-edit"
      : "daily-generation-table__cell--generator-edit";
  }

  protected getSourceLabel(source: DailyGenerationSource): string {
    const label = this.sourceOptions.find((option) => option.value === source)?.label ?? source;
    return `${label}(MWh)`;
  }

  protected formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '_';
    }
    return Number(value).toLocaleString('en-IN', {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
  }

  protected handleEntityTableScroll(event: Event): void {
    const tableWrap = event.currentTarget as HTMLElement | null;
    if (!tableWrap || this.isLoadingMoreEntities || !this.hasMoreEntities) {
      return;
    }

    const remainingScroll = tableWrap.scrollWidth - tableWrap.clientWidth - tableWrap.scrollLeft;
    if (remainingScroll <= HORIZONTAL_LOAD_THRESHOLD_PX) {
      this.loadReport(true);
    }
  }

  protected getTableColspan(): number {
    return 2 + Math.max(this.entities.length * (this.selectedSources.length + 1), 1);
  }

  protected trackByOption(_: number, option: DailyGenerationOption): number {
    return option.id;
  }

  protected trackByEntity(_: number, entity: DailyGenerationComparisonEntity): string {
    return entity.key;
  }

  protected trackByRow(_: number, row: DailyGenerationComparisonRow): string {
    return row.block_key;
  }

  protected trackBySource(_: number, source: DailyGenerationSource): string {
    return source;
  }

  private getPowerCell(row: DailyGenerationComparisonRow, entity: DailyGenerationComparisonEntity): DailyGenerationPowerCell {
    return row.power_cells?.[entity.key] ?? {
      value: row.power_values?.[entity.key] ?? null,
      source: null,
      editor_group: null,
    };
  }

  private bindFilterListeners(): void {
    this.filtersForm.get('target_type')?.valueChanges
      .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.selectedTargetIds = [];
        this.targetSearchTerm = '';
        this.loadReport();
      });

    this.filtersForm.get('data_frequency')?.valueChanges
      .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.applyFrequencyDefaults();
        this.loadReport();
      });

    const dateChanges = this.filtersForm.get('date')?.valueChanges;
    const fromDateChanges = this.filtersForm.get('from_date')?.valueChanges;
    const toDateChanges = this.filtersForm.get('to_date')?.valueChanges;
    if (!dateChanges || !fromDateChanges || !toDateChanges) {
      return;
    }

    merge(dateChanges, fromDateChanges, toDateChanges)
      .pipe(debounceTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clampDailyRange();
        this.loadReport();
      });
  }

  private listenToPreferenceChanges(): void {
    if (typeof window === 'undefined') {
      return;
    }

    fromEvent<CustomEvent<{ name?: string }>>(window, 'app-preferences-change')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.detail?.name !== 'showBsDate') {
          return;
        }

        this.closeBsDatePicker();
        void this.handleDateSystemChange();
      });
  }

  private async handleDateSystemChange(): Promise<void> {
    const targetMode = this.getDatePreferenceKey();
    await this.convertDateControls(targetMode);
    this.loadReport();
  }

  private async convertDateControls(targetMode: 'AD' | 'BS'): Promise<void> {
    const sourceMode = targetMode === 'BS' ? 'AD' : 'BS';
    const patch: Partial<Record<DailyGenerationDateControlName, string>> = {};
    for (const controlName of ['date', 'from_date', 'to_date'] as DailyGenerationDateControlName[]) {
      const value = this.getDateControlValue(controlName);
      const converted = await this.convertComparisonDate(value, sourceMode, targetMode);
      if (converted) {
        patch[controlName] = targetMode === 'BS' ? converted.bs_date_label : converted.data_date;
      }
    }

    if (Object.keys(patch).length) {
      this.filtersForm.patchValue(patch, { emitEvent: false });
    }
  }

  private async convertComparisonDate(dateValue: string, sourceMode: 'AD' | 'BS', targetMode: 'AD' | 'BS'): Promise<DailyGenerationDateConversionResponse | null> {
    if (!dateValue) {
      return null;
    }
    const apiDateValue = sourceMode === 'BS' ? parseBsDate(dateValue) : dateValue;

    try {
      const response = await firstValueFrom(
        this.httpSvc.requestCall(
          AuthEndPoints.CONVERT_LOG_SHEET_DATE,
          ApiMethod.POST,
          {
            data_date: apiDateValue,
            bs_ad_key: targetMode,
            source_bs_ad_key: sourceMode,
          },
          false,
          { suppressToast: true },
        ),
      );
      return this.extractDateConversion(response);
    } catch {
      return null;
    }
  }

  private async loadBsCalendarForControl(controlName: DailyGenerationDateControlName): Promise<void> {
    this.activeBsDateControl = controlName;
    this.bsCalendarOpen = true;
    await this.loadBsCalendar(this.getDateControlValue(controlName), this.getDatePreferenceKey());
  }

  private async loadBsCalendar(dateValue: string, bsAdKey: 'AD' | 'BS' = 'BS'): Promise<void> {
    if (!dateValue) {
      return;
    }

    this.bsCalendarLoading = true;
    try {
      const response = await firstValueFrom(
        this.httpSvc.requestCall(
          AuthEndPoints.GET_LOG_SHEET_BS_CALENDAR,
          ApiMethod.POST,
          {
            data_date: bsAdKey === 'BS' ? parseBsDate(dateValue) : dateValue,
            bs_ad_key: bsAdKey,
          },
          false,
          { suppressToast: true },
        ),
      );
      this.bsCalendarMonth = this.extractBsCalendar(response);
    } catch {
      this.bsCalendarMonth = null;
    } finally {
      this.bsCalendarLoading = false;
      this.cdr.detectChanges();
    }
  }

  private getDatePreferenceKey(): 'AD' | 'BS' {
    return this.preferencesSvc.showBsDate ? 'BS' : 'AD';
  }

  private getDateControlValue(controlName: DailyGenerationDateControlName): string {
    const value = this.filtersForm.get(controlName)?.value;
    return typeof value === 'string' ? value : '';
  }

  private applyFrequencyDefaults(): void {
    if (!this.isDailyFrequency()) {
      return;
    }
    const currentToDate = this.getDateControlValue('to_date') || getDateValue(new Date());
    const toDate = this.isBsDateMode ? parseBsDate(currentToDate) : currentToDate;
    const fromDate = shiftDate(toDate, -DAILY_DEFAULT_RANGE_DAYS);
    this.filtersForm.patchValue(
      { from_date: this.isBsDateMode ? formatBsDate(fromDate) : fromDate },
      { emitEvent: false },
    );
  }

  private clampDailyRange(): void {
    if (!this.isDailyFrequency()) {
      return;
    }
    const fromDateValue = this.filtersForm.get('from_date')?.value;
    const toDateValue = this.filtersForm.get('to_date')?.value;
    const fromDate = this.isBsDateMode ? parseBsDate(fromDateValue || '') : fromDateValue;
    const toDate = this.isBsDateMode ? parseBsDate(toDateValue || '') : toDateValue;
    if (!fromDate || !toDate) {
      return;
    }
    if (fromDate > toDate) {
      this.filtersForm.patchValue(
        { from_date: this.isBsDateMode ? formatBsDate(toDate) : toDate },
        { emitEvent: false },
      );
      return;
    }
    const maxFromDate = shiftDate(toDate, -DAILY_MAX_RANGE_DAYS);
    if (fromDate < maxFromDate) {
      this.filtersForm.patchValue(
        { from_date: this.isBsDateMode ? formatBsDate(maxFromDate) : maxFromDate },
        { emitEvent: false },
      );
    }
  }

  private loadGeneratorOptions(): void {
    this.loadTargetOptionPage('generator', true);
    this.loadTargetOptionPage('substation', true);
  }

  private loadTargetOptionPage(targetType: DailyGenerationTargetType, reset = false): void {
    const isSubstation = targetType === 'substation';
    const isLoading = isSubstation ? this.isLoadingSubstationOptions : this.isLoadingGeneratorOptions;
    if (isLoading) {
      return;
    }
    const currentPage = isSubstation ? this.substationOptionPage : this.generatorOptionPage;
    const hasMore = isSubstation ? this.hasMoreSubstationOptions : this.hasMoreGeneratorOptions;
    if (!reset && !hasMore) {
      return;
    }
    const page = reset ? 1 : currentPage + 1;
    if (reset) {
      if (isSubstation) {
        this.substationOptions = [];
        this.substationOptionPage = 0;
        this.hasMoreSubstationOptions = true;
      } else {
        this.generatorOptions = [];
        this.generatorOptionPage = 0;
        this.hasMoreGeneratorOptions = true;
      }
    }
    if (isSubstation) {
      this.isLoadingSubstationOptions = true;
    } else {
      this.isLoadingGeneratorOptions = true;
    }
    const endpoint = isSubstation ? AuthEndPoints.GET_SUBSTATIONS_LIST : AuthEndPoints.GET_GENERATORS_LIST;
    this.httpSvc
      .requestCall(endpoint, ApiMethod.GET, '?page_no=' + page + '&page_size=' + FILTER_OPTION_PAGE_SIZE, false, { suppressToast: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (isSubstation) {
            this.isLoadingSubstationOptions = false;
          } else {
            this.isLoadingGeneratorOptions = false;
          }
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const options = isSubstation ? this.extractSubstationOptions(response) : this.extractGeneratorOptions(response);
          if (isSubstation) {
            this.substationOptions = this.mergeTargetOptions(this.substationOptions, options);
            this.substationOptionPage = page;
            this.hasMoreSubstationOptions = this.hasMoreTargetOptions(response, options);
          } else {
            this.generatorOptions = this.mergeTargetOptions(this.generatorOptions, options);
            this.generatorOptionPage = page;
            this.hasMoreGeneratorOptions = this.hasMoreTargetOptions(response, options);
          }
        },
        error: () => {
          if (isSubstation) {
            this.hasMoreSubstationOptions = false;
          } else {
            this.hasMoreGeneratorOptions = false;
          }
        },
      });
  }

  private mergeTargetOptions(existing: DailyGenerationOption[], nextPage: DailyGenerationOption[]): DailyGenerationOption[] {
    const optionsById = new Map(existing.map((option) => [option.id, option]));
    nextPage.forEach((option) => optionsById.set(option.id, option));
    return [...optionsById.values()];
  }

  private hasMoreTargetOptions(response: unknown, pageOptions: DailyGenerationOption[]): boolean {
    const value = this.extractResponseValue(response);
    const pagination = this.getRecord(value?.['pagination']);
    const page = this.normalizeNumber(pagination['page_no']);
    const totalPages = this.normalizeNumber(pagination['total_pages']);
    if (page !== null && totalPages !== null) {
      return page < totalPages;
    }
    return pageOptions.length === FILTER_OPTION_PAGE_SIZE;
  }

  private loadReport(append = false): void {
    if (!this.selectedSources.length) {
      this.rows = [];
      this.entities = [];
      this.hasMoreEntities = false;
      this.isLoadingMoreEntities = false;
      this.errorMessage = 'Select at least one data source.';
      return;
    }
    if (append && this.isLoadingMoreEntities) {
      return;
    }

    const requestSequence = ++this.reportRequestSequence;
    if (append) {
      this.isLoadingMoreEntities = true;
    } else {
      this.isLoading = true;
      this.isLoadingMoreEntities = false;
    }
    this.errorMessage = '';
    const entityOffset = append ? this.entities.length : 0;
    this.httpSvc
      .requestCall(
        AuthEndPoints.GET_DAILY_GENERATION_COMPARISON_REPORT,
        ApiMethod.POST,
        this.buildReportPayload(entityOffset, ENTITY_PAGE_SIZE),
        false,
        { suppressToast: true },
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          if (requestSequence !== this.reportRequestSequence) {
            return;
          }
          if (append) {
            this.isLoadingMoreEntities = false;
          } else {
            this.isLoading = false;
          }
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          if (requestSequence === this.reportRequestSequence) {
            this.applyReport(response, append);
            this.cdr.detectChanges();
          }
        },
        error: () => {
          if (requestSequence !== this.reportRequestSequence) {
            return;
          }
          if (!append) {
            this.entities = [];
            this.rows = [];
            this.hasMoreEntities = false;
          }
          this.errorMessage = 'Unable to load daily generation comparison.';
        },
      });
  }

  private buildReportPayload(entityOffset = 0, entityLimit: number | undefined = ENTITY_PAGE_SIZE): Record<string, unknown> {
    const frequency = this.getSelectedFrequency();
    const targetType = this.getSelectedTargetType();
    const date = this.filtersForm.get('date')?.value;
    return {
      data_frequency: frequency,
      from_date: this.toApiDateValue(frequency === 'DAILY' ? this.filtersForm.get('from_date')?.value : date),
      to_date: this.toApiDateValue(frequency === 'DAILY' ? this.filtersForm.get('to_date')?.value : date),
      target_type: targetType,
      generator_ids: targetType === 'generator' ? this.selectedTargetIds : [],
      substation_ids: targetType === 'substation' ? this.selectedTargetIds : [],
      data_sources: this.selectedSources,
      bs_ad_key: this.getDatePreferenceKey(),
      entity_offset: entityOffset,
      entity_limit: entityLimit,
    };
  }

  private toApiDateValue(value: unknown): string {
    const dateValue = getPayloadDateString(value);
    return this.isBsDateMode ? parseBsDate(dateValue) : dateValue;
  }

  private buildExportFileName(): string {
    const frequency = this.getSelectedFrequency().toLowerCase();
    const payload = this.buildReportPayload();
    const fromDate = getPayloadDateString(payload['from_date']);
    const toDate = getPayloadDateString(payload['to_date']);
    return `daily_generation_comparison_${frequency}_${fromDate}_${toDate}.xlsx`;
  }

  private applyReport(response: unknown, append = false): void {
    const value = this.extractResponseValue(response) as Partial<DailyGenerationComparisonResponse> | null;
    if (!value) {
      if (!append) {
        this.entities = [];
        this.rows = [];
        this.hasMoreEntities = false;
      }
      return;
    }
    const sourceOptions = this.getArrayValue(value, "source_options") as DailyGenerationSourceOption[];
    const selectedSources = this.getArrayValue(value, "selected_sources") as DailyGenerationSource[];
    const pageEntities = this.getArrayValue(value, "entities") as DailyGenerationComparisonEntity[];
    const pageRows = this.getArrayValue(value, "rows") as DailyGenerationComparisonRow[];
    this.sourceOptions = sourceOptions.length ? sourceOptions : DEFAULT_SOURCE_OPTIONS;
    if (selectedSources.length) {
      this.selectedSources = selectedSources;
    }
    this.entities = append ? [...this.entities, ...pageEntities] : pageEntities;
    this.rows = append ? this.mergeComparisonRows(this.rows, pageRows) : pageRows;
    this.hasMoreEntities = value.has_more_entities ?? (this.entities.length < Number(value.total_entities ?? this.entities.length));
  }

  private mergeComparisonRows(existingRows: DailyGenerationComparisonRow[], pageRows: DailyGenerationComparisonRow[]): DailyGenerationComparisonRow[] {
    const mergedRows = new Map(existingRows.map((row) => [row.block_key, { ...row }]));
    for (const pageRow of pageRows) {
      const existingRow = mergedRows.get(pageRow.block_key);
      if (!existingRow) {
        mergedRows.set(pageRow.block_key, { ...pageRow });
        continue;
      }
      existingRow.power_values = { ...existingRow.power_values, ...pageRow.power_values };
      existingRow.power_cells = { ...existingRow.power_cells, ...pageRow.power_cells };
      existingRow.values = { ...existingRow.values, ...pageRow.values };
    }
    return [...mergedRows.values()];
  }

  private extractDateConversion(response: unknown): DailyGenerationDateConversionResponse | null {
    const value = this.extractResponseValue(response);
    const selectedDate = this.extractStringValue(value ?? {}, 'selected_date');
    if (!selectedDate) {
      return null;
    }

    return {
      data_date: this.extractStringValue(value ?? {}, 'data_date') ?? '',
      bs_date: this.extractStringValue(value ?? {}, 'bs_date') ?? '',
      bs_date_label: this.extractStringValue(value ?? {}, 'bs_date_label') ?? '',
      selected_date: selectedDate,
      selected_date_label: this.extractStringValue(value ?? {}, 'selected_date_label') ?? selectedDate,
    };
  }

  private extractBsCalendar(response: unknown): DailyGenerationBsCalendarResponse | null {
    const value = this.extractResponseValue(response);
    const bsYear = this.normalizeNumber(value?.['bs_year']);
    const bsMonth = this.normalizeNumber(value?.['bs_month']);
    if (!value || bsYear === null || bsMonth === null) {
      return null;
    }

    return {
      data_date: this.extractStringValue(value, 'data_date') ?? '',
      bs_date: this.extractStringValue(value, 'bs_date') ?? '',
      bs_year: bsYear,
      bs_month: bsMonth,
      bs_month_name: this.extractStringValue(value, 'bs_month_name') ?? '',
      selected_date: this.extractStringValue(value, 'selected_date') ?? '',
      selected_date_label: this.extractStringValue(value, 'selected_date_label') ?? '',
      cells: this.getArrayValue(value, 'cells') as DailyGenerationBsCalendarCell[],
    };
  }

  protected getTargetOptions(): DailyGenerationOption[] {
    return this.getSelectedTargetType() === 'substation' ? this.substationOptions : this.generatorOptions;
  }

  private extractGeneratorOptions(response: unknown): DailyGenerationOption[] {
    const value = this.extractResponseValue(response);
    return this.getArrayValue(value, 'generators')
      .map((item) => {
        const record = this.getRecord(item);
        return {
          id: this.normalizeNumber(record['id']) ?? 0,
          name: this.extractStringValue(record, 'generator_name') ?? '-',
          code: this.extractStringValue(record, 'scada_station_code'),
        };
      })
      .filter((option) => option.id > 0);
  }

  private extractSubstationOptions(response: unknown): DailyGenerationOption[] {
    const value = this.extractResponseValue(response);
    return this.getArrayValue(value, 'substations')
      .map((item) => {
        const record = this.getRecord(item);
        return {
          id: this.normalizeNumber(record['id']) ?? 0,
          name: this.extractStringValue(record, 'station_name') ?? '-',
          code: this.extractStringValue(record, 'station_code') ?? this.extractStringValue(record, 'scada_code'),
        };
      })
      .filter((option) => option.id > 0)
      .sort((first, second) => first.name.localeCompare(second.name));
  }

  private extractResponseValue(response: unknown): Record<string, unknown> | null {
    return this.findReportPayload(response);
  }

  private findReportPayload(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const record = this.getRecord(value);
    if (Array.isArray(record['entities']) || Array.isArray(record['rows']) || Array.isArray(record['blocks'])) {
      return record;
    }

    for (const key of ['value', 'data', 'result']) {
      const nested = this.findReportPayload(record[key]);
      if (nested) {
        return nested;
      }
    }

    return record;
  }

  private getArrayValue(record: Record<string, unknown> | null, key: string): unknown[] {
    const value = record?.[key];
    return Array.isArray(value) ? value : [];
  }

  private getRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
  }

  private extractStringValue(record: Record<string, unknown>, key: string): string | null {
    const value = record[key];
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private normalizeNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const normalized = Number(value);
    return Number.isFinite(normalized) ? normalized : null;
  }
}
